import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation } from '@react-navigation/native';
import { useState } from 'react';
import {
    ActivityIndicator,
    Alert,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getThemeColors } from '../utils/themeStyles';
import { useMissions } from '../context/MissionsContext';

export default function CartScreen() {
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const { user } = useAuth();
  const colors = getThemeColors(isDarkTheme);
  const { cartItems, removeFromCart, checkout } = useMissions();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [notice, setNotice] = useState('');

  // Route de la liste des missions selon le mode (connecté -> tabs 'Main', invité -> 'PublicMissions').
  const missionsRoute = user ? 'Main' : 'PublicMissions';
  // On cible explicitement l'écran 'Missions' à l'intérieur de 'Main' : en desktop,
  // le panier est ouvert via un reset de la pile (InnerStack = [Cart] seul), donc
  // un simple navigate('Main') resterait sur Cart. (cf. même pattern au checkout.)
  const goToMissions = () => navigation.navigate(missionsRoute as never, { screen: 'Missions' } as never);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return { text: '#10B981', background: '#10B98122' };
      case 'Medium': return { text: '#F59E0B', background: '#F59E0B22' };
      case 'Hard': return { text: '#EF4444', background: '#EF444422' };
      default: return { text: colors.textSecondary, background: colors.surfaceSecondary };
    }
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const savings = cartItems.reduce((sum, item) => {
    if (item.isOnSale && item.originalPrice) return sum + (item.originalPrice - item.price) * item.quantity;
    return sum;
  }, 0);
  const tax = subtotal * 0.08;
  const total = subtotal + tax;

  const handleBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      goToMissions();
    }
  };
  const handleCheckout = async () => {
    if (isCheckingOut) return;
    // Le checkout EXIGE l'authentification : si déconnecté, on redirige
    // directement vers l'écran de connexion (Alert peu fiable sur web).
    if (!user) {
      // Revenir au panier après authentification (le panier invité y sera fusionné).
      if (typeof window !== 'undefined') {
        try {
          sessionStorage.setItem('ccjaw.returnTo', '/cart');
        } catch {
          /* ignore */
        }
      }
      navigation.navigate('Login' as never);
      return;
    }
    setIsCheckingOut(true);
    try {
      const result = await checkout();
      // Sécurité : si l'API/contexte signale needsLogin, rediriger aussi.
      if (result?.needsLogin) {
        navigation.navigate('Login' as never);
        return;
      }
      // Redirection Stripe en cours (web) : la page va partir, rien à faire.
      if (result?.redirected) return;
      if (result?.status === 'completed') {
        // Achat réussi : commande créée + missions activées + panier vidé.
        // Feedback FIABLE sur web (les onPress d'Alert ne se déclenchent pas en
        // react-native-web) : message simple PUIS navigation directe vers les
        // missions actives, pour que l'utilisateur voie sa mission activée.
        Alert.alert('Commande confirmée', 'Vos missions ont été activées. Bonne formation !');
        navigation.navigate(missionsRoute as never, { screen: 'Missions' } as never);
        return;
      } else if (result?.checkout_url) {
        // Natif : ouverture gérée par checkout() ; web : redirigé. Rien à faire.
      } else {
        Alert.alert('Paiement en attente', 'Le paiement n\'a pas pu être finalisé. Réessayez.');
      }
    } catch (_) {
      Alert.alert('Erreur', 'Le paiement a échoué. Réessayez plus tard.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleRemove = (id: number) => {
    // Suppression directe (pas de popup de confirmation) + message transitoire.
    removeFromCart(id);
    setNotice('Article retiré du panier.');
    setTimeout(() => setNotice(''), 2500);
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Ionicons name="arrow-back" size={20} color={colors.iconPrimary} />
          </TouchableOpacity>
          <View style={styles.headerText}>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>Shopping Cart</Text>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
              {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in cart
            </Text>
          </View>
          <View style={styles.cartIcon}>
            <Ionicons name="cart" size={20} color={colors.iconSecondary} />
          </View>
        </View>
      </View>

      {notice ? (
        <View style={[styles.notice, { backgroundColor: colors.surfaceSecondary, borderColor: colors.borderSecondary }]}>
          <Ionicons name="checkmark-circle" size={16} color={colors.success} />
          <Text style={[styles.noticeText, { color: colors.textSecondary }]}>{notice}</Text>
        </View>
      ) : null}

      <ScrollView style={[styles.content, { backgroundColor: colors.background }]}>
        {cartItems.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="cart-outline" size={64} color={colors.textTertiary} />
            <Text style={[styles.emptyTitle, { color: colors.textPrimary }]}>Your cart is empty</Text>
            <Text style={[styles.emptySubtitle, { color: colors.textSecondary }]}>
              Browse the marketplace to find missions to add to your cart
            </Text>
            <TouchableOpacity style={[styles.continueButton, { backgroundColor: colors.primary }]} onPress={goToMissions}>
              <Text style={[styles.continueButtonText, { color: colors.badgeText }]}>Continue Shopping</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.itemsContainer}>
            {cartItems.map((item) => (
              <View key={item.id} style={[styles.itemCard, { backgroundColor: colors.cardBackground }]}>
                <View style={styles.itemContent}>
                  <View style={styles.itemHeader}>
                    <Text style={[styles.itemTitle, { color: colors.textPrimary }]} numberOfLines={2}>{item.title}</Text>
                    <TouchableOpacity onPress={() => handleRemove(item.id)} style={styles.removeButton}>
                      <Ionicons name="trash" size={16} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                  <Text style={[styles.itemDescription, { color: colors.textSecondary }]} numberOfLines={2}>
                    {item.description}
                  </Text>
                  <View style={styles.itemMeta}>
                    <View style={[styles.difficultyBadge, { backgroundColor: getDifficultyColor(item.difficulty).background }]}>
                      <Text style={[styles.difficultyText, { color: getDifficultyColor(item.difficulty).text }]}>{item.difficulty}</Text>
                    </View>
                    <Text style={[styles.durationText, { color: colors.textSecondary }]}>{item.duration}</Text>
                  </View>
                  <View style={styles.itemFooter}>
                    <View style={styles.priceContainer}>
                      {item.isOnSale && item.originalPrice && (
                        <Text style={[styles.originalPrice, { color: colors.textTertiary }]}>${item.originalPrice}</Text>
                      )}
                      <Text style={[styles.price, { color: colors.textPrimary }]}>${item.price}</Text>
                    </View>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      {cartItems.length > 0 && (
        <View style={[styles.summary, { backgroundColor: colors.surface, borderTopColor: colors.border }]}>
          <View style={styles.summaryContent}>
            <View style={styles.summaryRow}>
              <Text style={[styles.summaryLabel, { color: colors.textSecondary }]}>Subtotal</Text>
              <Text style={[styles.summaryValue, { color: colors.textPrimary }]}>${subtotal.toFixed(2)}</Text>
            </View>
            {savings > 0 && (
              <View style={styles.summaryRow}>
                <Text style={[styles.savingsLabel, { color: colors.success }]}>Savings</Text>
                <Text style={[styles.savingsValue, { color: colors.success }]}>-${savings.toFixed(2)}</Text>
              </View>
            )}
            <View style={styles.summaryRow}>
              <Text style={[styles.summaryLabel, { color: colors.textSecondary }]}>Tax (8%)</Text>
              <Text style={[styles.summaryValue, { color: colors.textPrimary }]}>${tax.toFixed(2)}</Text>
            </View>
            <View style={[styles.separator, { backgroundColor: colors.border }]} />
            <View style={styles.summaryRow}>
              <Text style={[styles.totalLabel, { color: colors.textPrimary }]}>Total</Text>
              <Text style={[styles.totalValue, { color: colors.textPrimary }]}>${total.toFixed(2)}</Text>
            </View>
            <TouchableOpacity
              style={[styles.checkoutButton, { backgroundColor: colors.primary, opacity: isCheckingOut ? 0.7 : 1 }]}
              onPress={handleCheckout}
              disabled={isCheckingOut}
            >
              {isCheckingOut ? (
                <ActivityIndicator size="small" color={colors.badgeText} />
              ) : (
                <Ionicons name="card" size={16} color={colors.badgeText} />
              )}
              <Text style={[styles.checkoutButtonText, { color: colors.badgeText }]}>
                {isCheckingOut ? 'Traitement...' : 'Proceed to Checkout'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { padding: 16, borderBottomWidth: 1 },
  headerContent: { flexDirection: 'row', alignItems: 'center' },
  backButton: { padding: 8, marginRight: 12 },
  headerText: { flex: 1 },
  headerTitle: { fontSize: 18, fontWeight: 'bold' },
  headerSubtitle: { fontSize: 14, marginTop: 2 },
  cartIcon: { padding: 8 },
  content: { flex: 1 },
  notice: { flexDirection: 'row', alignItems: 'center', gap: 8, marginHorizontal: 16, marginTop: 12, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1 },
  noticeText: { fontSize: 14, fontWeight: '500' },
  emptyState: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
  emptyTitle: { fontSize: 18, fontWeight: 'bold', marginTop: 16, marginBottom: 8 },
  emptySubtitle: { fontSize: 14, textAlign: 'center', marginBottom: 24 },
  continueButton: { paddingVertical: 12, paddingHorizontal: 24, borderRadius: 8 },
  continueButtonText: { fontSize: 16, fontWeight: '600' },
  itemsContainer: { padding: 16 },
  itemCard: { borderRadius: 12, padding: 16, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 2, elevation: 2 },
  itemContent: { gap: 12 },
  itemHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  itemTitle: { fontSize: 16, fontWeight: '600', flex: 1, marginRight: 8 },
  removeButton: { padding: 4 },
  itemDescription: { fontSize: 14, lineHeight: 20 },
  itemMeta: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  difficultyBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4 },
  difficultyText: { fontSize: 12, fontWeight: '500' },
  durationText: { fontSize: 12 },
  itemFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  priceContainer: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  originalPrice: { fontSize: 14, textDecorationLine: 'line-through' },
  price: { fontSize: 16, fontWeight: '600' },
  summary: { borderTopWidth: 1 },
  summaryContent: { padding: 16, gap: 12 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  summaryLabel: { fontSize: 14 },
  summaryValue: { fontSize: 14 },
  savingsLabel: { fontSize: 14 },
  savingsValue: { fontSize: 14, fontWeight: '600' },
  separator: { height: 1 },
  totalLabel: { fontSize: 16, fontWeight: '600' },
  totalValue: { fontSize: 16, fontWeight: '600' },
  checkoutButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 16, borderRadius: 8, gap: 8 },
  checkoutButtonText: { fontSize: 16, fontWeight: '600' },
});

